/**
 * Fetch wrapper. Every API call goes through this so authentication and error
 * handling stay consistent.
 *
 * - `credentials: "include"` so the oauth_id_token cookie rides along.
 * - 401 → kick the user to /api/auth/login (full-page navigation so the
 *   browser follows the IdP redirect and lands back with a fresh cookie).
 * - Non-2xx → throw ApiError so TanStack Query surfaces it.
 */

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly payload?: unknown,
  ) {
    super(message);
  }
}

const BASE = import.meta.env.VITE_API_BASE ?? "/api";

export interface ApiFetchInit extends Omit<RequestInit, "body"> {
  body?: unknown;
  /** If true, skip the auto-redirect on 401. Useful for /api/me where the
   *  AuthProvider wants to render an unauthenticated state. */
  skipAuthRedirect?: boolean;
}

export async function apiFetch<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const { body, skipAuthRedirect, headers, ...rest } = init;
  const url = path.startsWith("http") ? path : `${BASE}${path.startsWith("/") ? path : `/${path}`}`;

  const finalHeaders: Record<string, string> = {
    Accept: "application/json",
    ...(headers as Record<string, string> | undefined),
  };
  let finalBody: BodyInit | undefined;
  if (body !== undefined) {
    if (body instanceof FormData || body instanceof Blob) {
      finalBody = body;
    } else {
      finalHeaders["Content-Type"] ??= "application/json";
      finalBody = JSON.stringify(body);
    }
  }

  const resp = await fetch(url, {
    credentials: "include",
    headers: finalHeaders,
    body: finalBody,
    ...rest,
  });

  if (resp.status === 401 && !skipAuthRedirect) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `${BASE}/auth/login?next=${next}`;
    return new Promise<T>(() => {}); // resolve never — page is navigating.
  }

  if (!resp.ok) {
    let payload: unknown = undefined;
    let code = "http_error";
    let message = resp.statusText;
    try {
      payload = await resp.json();
      if (payload && typeof payload === "object") {
        const p = payload as { code?: string; message?: string; detail?: unknown };
        code = p.code ?? code;
        // Both our custom AppError ({code, message, ...}) and FastAPI's
        // HTTPException ({detail: "..."}) come through here — surface
        // whichever the server sent so error toasts/pages aren't blank.
        const detail =
          typeof p.detail === "string"
            ? p.detail
            : p.detail != null
              ? JSON.stringify(p.detail)
              : undefined;
        message = p.message ?? detail ?? message;
      }
    } catch {
      /* non-JSON body — keep defaults */
    }
    throw new ApiError(resp.status, code, message, payload);
  }

  if (resp.status === 204) return undefined as T;
  return (await resp.json()) as T;
}

/**
 * POST and return the raw {@link Response} for streaming (Server-Sent Events);
 * the caller reads `resp.body`. Shares apiFetch's cookie auth, 401 redirect, and
 * ApiError-on-non-2xx behavior.
 */
export async function apiStream(path: string, init: ApiFetchInit = {}): Promise<Response> {
  const { body, skipAuthRedirect, headers, ...rest } = init;
  const url = path.startsWith("http") ? path : `${BASE}${path.startsWith("/") ? path : `/${path}`}`;

  const finalHeaders: Record<string, string> = {
    Accept: "text/event-stream",
    ...(headers as Record<string, string> | undefined),
  };
  let finalBody: BodyInit | undefined;
  if (body !== undefined) {
    finalHeaders["Content-Type"] ??= "application/json";
    finalBody = JSON.stringify(body);
  }

  const resp = await fetch(url, { credentials: "include", headers: finalHeaders, body: finalBody, ...rest });

  if (resp.status === 401 && !skipAuthRedirect) {
    const next = encodeURIComponent(window.location.pathname + window.location.search);
    window.location.href = `${BASE}/auth/login?next=${next}`;
    return new Promise<Response>(() => {});
  }
  if (!resp.ok) {
    let message = resp.statusText;
    let code = "http_error";
    try {
      const payload = (await resp.json()) as { code?: string; message?: string; detail?: unknown };
      code = payload.code ?? code;
      const detail = typeof payload.detail === "string" ? payload.detail : undefined;
      message = payload.message ?? detail ?? message;
    } catch {
      /* non-JSON body */
    }
    throw new ApiError(resp.status, code, message);
  }
  return resp;
}
